import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, MapPin, X, Sparkles, Flag } from "lucide-react";
import { TarotCard } from "../components/TarotCard";
import { AceHeart } from "../components/AceHeart";
import { ReportModal } from "../components/ReportModal";
import { BlockButton } from "../components/BlockButton";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { AccessExpired } from "../components/AccessExpired";
import { toast } from "sonner";

export default function Discover() {
  const [deck, setDeck] = useState(null);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [expired, setExpired] = useState(false);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    api.get("/discover").then((r) => setDeck(r.data)).catch((e) => {
      if (e?.response?.status === 402) setExpired(true);
      else toast.error(formatApiError(e));
    });
  }, []);

  const card = deck?.[index];
  const [acting, setActing] = useState(false);

  const next = () => {
    setFlipped(false);
    setTimeout(() => setIndex((i) => i + 1), 200);
  };

  const like = async () => {
    if (acting || !card) return;
    setActing(true);
    try {
      const { data } = await api.post(`/likes/${card.id}`);
      if (data.matched) setMatch(data.match);
      next();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  if (deck === null) {
    if (expired) return <AccessExpired />;
    return <div className="flex min-h-[60vh] items-center justify-center text-sm text-stone2" data-testid="discover-loading">Nous préparons vos profils…</div>;
  }

  // Photo de profil obligatoire pour voir les autres membres
  if (user && !user.avatar) {
    return (
      <div className="relative mx-auto max-w-md px-4 py-20 text-center" data-testid="discover-photo-required">
        <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.07]" />
        <div className="relative rounded-3xl border border-terra/15 bg-white p-10 shadow-card">
          <p className="font-cinzel text-xs tracking-[0.35em] text-gold">DERNIÈRE ÉTAPE</p>
          <h1 className="mt-3 font-serif text-2xl font-semibold text-ink">Votre photo d'abord</h1>
          <p className="mt-3 text-sm leading-relaxed text-clay">
            Pour découvrir les autres membres, ajoutez votre photo de profil — elle est obligatoire et nous aide à garder une communauté bienveillante, sans faux profils.
          </p>
          <Link to="/profile" data-testid="discover-complete-profile-link" className="mt-6 inline-block rounded-full bg-terra px-7 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark">
            Compléter mon profil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="discover-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.07]" />

      <div className="relative text-center">
        <p className="font-cinzel text-xs tracking-[0.35em] text-gold">LES RENCONTRES</p>
        <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Vos coups de cœur affinitaires</h1>
        <p className="mt-2 text-sm text-stone2">Uniquement des profils dont les affinités répondent aux vôtres — retournez la carte pour lire leur histoire.</p>
      </div>

      <div className="relative mx-auto mt-10 max-w-sm">
        {card ? (
          <>
            {deck[index + 1] && (
              <div className="absolute inset-x-4 -bottom-3 top-3 rotate-2 opacity-60">
                <div className="h-full w-full rounded-2xl border border-terra/20 bg-parch shadow-boho" />
              </div>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 40, rotate: -2 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                exit={{ opacity: 0, x: -80, rotate: -6 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <TarotCard
                  testid="discover-card"
                  variant={1}
                  flipped={flipped}
                  onClick={() => setFlipped((f) => !f)}
                  className="aspect-[5/8]"
                >
                  <div className="flex h-full flex-col">
                    {card.avatar && (
                      <div className="relative h-[46%] overflow-hidden">
                        <img src={card.avatar} alt={card.name} className="h-full w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col items-center justify-between p-5 text-center">
                      <div>
                        <h3 className="font-serif text-2xl font-semibold text-ink" data-testid="discover-card-name">
                          {card.name}{card.age ? `, ${card.age}` : ""}
                        </h3>
                        {card.city && (
                          <p className="mt-1 flex items-center justify-center gap-1 text-xs text-stone2">
                            <MapPin size={12} /> {card.city}
                          </p>
                        )}
                      </div>
                      <div className="my-2">
                        <span className="font-cinzel text-[10px] tracking-[0.3em] text-gold">{card.archetype_label}</span>
                        <p className="mt-2 text-xs leading-relaxed text-clay">{card.bio}</p>
                      </div>
                      {card.compatibility != null && (
                        <span className="rounded-full border border-gold/50 bg-gold-light px-3 py-1 text-xs font-semibold text-ink" data-testid="discover-compatibility">
                          Compatibilité · {card.compatibility}%
                        </span>
                      )}
                    </div>
                  </div>
                </TarotCard>
              </motion.div>
            </AnimatePresence>

            <div className="relative mt-8 flex items-center justify-center gap-5">
              <button
                onClick={next}
                data-testid="discover-pass-button"
                className="flex h-14 w-14 items-center justify-center rounded-full border border-terra/25 bg-white text-clay shadow-boho transition-all duration-300 hover:scale-105 hover:text-terra"
                title="Passer"
              >
                <X size={22} />
              </button>
              <button
                onClick={like}
                disabled={acting}
                data-testid="discover-like-button"
                className="flex h-16 w-16 items-center justify-center rounded-full bg-terra text-cream shadow-card transition-all duration-300 hover:scale-110 hover:bg-terra-dark disabled:opacity-50"
                title="Coup de cœur"
              >
                <Heart size={26} fill="currentColor" />
              </button>
            </div>
            {/* Signalement & blocage — sécurité des membres */}
            <div className="relative mt-4 flex items-center justify-center gap-3" data-testid="discover-moderation">
              <button
                onClick={() => setReporting(true)}
                data-testid="discover-report-button"
                className="flex items-center gap-1.5 rounded-full border border-terra/20 bg-white px-4 py-2 text-xs font-semibold text-clay transition-colors hover:border-terra hover:text-terra"
              >
                <Flag size={13} /> Signaler
              </button>
              <BlockButton targetId={card.id} targetName={card.name} onBlocked={next} />
            </div>
            <p className="mt-6 text-center text-xs text-stone2">
              {deck.length - index} profil{deck.length - index > 1 ? "s" : ""} à découvrir
            </p>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl border border-terra/15 bg-white text-center shadow-boho" data-testid="discover-empty">
            <div className="relative h-36">
              <img src="/assets/couple-beach-hand.jpeg" alt="Couple main dans la main sur la plage" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
            </div>
            <div className="px-10 pb-10">
              <Sparkles size={32} className="mx-auto -mt-4 text-gold" />
              <h2 className="mt-4 font-serif text-2xl font-medium text-ink">C'est tout pour aujourd'hui</h2>
              <p className="mx-auto mt-3 max-w-xs text-sm text-clay">« Il suffit parfois d'un instant pour que tout commence. » <AceHeart size={15} className="mb-0.5 inline-block" /><br />Vous avez découvert tous les profils du moment — vos coups de cœur vous attendent peut-être déjà.</p>
              <Link to="/matches" data-testid="discover-matches-link" className="mt-6 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:bg-terra-dark">
                Voir mes matchs
              </Link>
            </div>
          </motion.div>
        )}
      </div>

      {/* Match modal */}
      <AnimatePresence>
        {match && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-plum/60 px-4 backdrop-blur-sm"
            data-testid="match-modal"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, rotateY: 40 }}
              animate={{ scale: 1, opacity: 1, rotateY: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-sm rounded-3xl border border-gold/60 bg-cream p-8 text-center shadow-card"
            >
              <Sparkles size={30} className="mx-auto text-gold" />
              <h2 className="mt-4 font-serif text-3xl font-medium text-ink" data-testid="match-title">C'est un match !</h2>
              <p className="mt-2 text-sm text-clay">
                Vous et <strong className="text-ink">{match.name}</strong> vous êtes mutuellement choisis.
              </p>
              {match.compatibility != null && (
                <p className="mt-3 font-cinzel text-xs tracking-[0.3em] text-terra">COMPATIBILITÉ · {match.compatibility}%</p>
              )}
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/matches" data-testid="match-message-button" className="rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:bg-terra-dark">
                  Envoyer un message
                </Link>
                <button onClick={() => setMatch(null)} data-testid="match-continue-button" className="rounded-full border border-terra/25 px-6 py-3 text-sm font-semibold text-terra transition-colors hover:bg-parch">
                  Continuer à découvrir
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modale de signalement */}
      {reporting && card && (
        <ReportModal target={card} context="profil" onClose={(sent) => { setReporting(false); if (sent) next(); }} />
      )}
    </div>
  );
}
